export type WorkspaceRole = 'workspace_owner' | 'company_client'

export const ENRICHMENT_STATUS = {
  PENDING: 0,
  ENRICHING: 1,
  ENRICHED: 2,
  FAILED: 3,
} as const

export type EnrichmentStatus = typeof ENRICHMENT_STATUS[keyof typeof ENRICHMENT_STATUS]

export const PROVINCES: Record<string, string> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
}

export const WORKSPACE_ROLE = {
  OWNER: 'workspace_owner' as const,
  CLIENT: 'company_client' as const,
} satisfies Record<string, WorkspaceRole>

export interface User {
  id: string
  name: string
  email: string
  is_admin: boolean
  contact_id: string | null
  email_verified_at: string | null
  created_at: string
  updated_at: string
  workspaces?: Workspace[]
}

export interface WorkspacePivot {
  role: WorkspaceRole
}

export interface Workspace {
  id: string
  name: string
  description: string | null
  website: string | null
  created_at: string
  updated_at: string
  pivot?: WorkspacePivot
}

export interface ActivityEvent {
  id: number
  description: string
  subject_type: string | null
  subject_name: string | null
  causer_name: string
  event: string | null
  changes: Record<string, { old: unknown; new: unknown }> | null
  workspace_name?: string | null
  created_at: string
}

export interface PaginatedResponse<T> {
  data: T[]
  meta: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
}

export interface UnmatchedBounce {
  id: string
  subject: string | null
  from_email: string | null
  failed_recipient: string | null
  reason: string | null
  received_at: string | null
  created_at: string
  workspace_id: string | null
  company_id: string | null
  company_name: string | null
  campaign_id: string | null
  campaign_status: CampaignStatus | null
}

export interface DashboardData {
  companies: { total: number; enriched: number; pending_enrichment: number }
  campaigns: { total: number; by_status: Record<string, number> }
  emails: { total_sent: number; total_replied: number; response_rate: number }
  pipeline_value: { total_ask_cents: number; by_tier: Record<string, number> }
}

export interface Contact {
  id: string
  company_id: string
  name: string | null
  email: string
  email_status: 'valid' | 'invalid'
  is_primary: boolean
  created_at: string
  updated_at: string
}

export interface CompanyEnrichmentData {
  description: string | null
  industry: string | null
  location: string | null
  size: string | null
  revenue_tier: string | null
  decision_maker: {
    name: string | null
    title: string | null
    email: string | null
    email_confidence: 'confirmed' | 'inferred' | 'unknown'
  } | null
  community_angle: string | null
  ask_tier: 'presented_by' | 'practice' | 'warm_up' | 'team_photo' | 'rally_towel' | 'gold_community' | 'silver_community' | 'bronze_community' | 'community_partner' | 'in_kind' | null
  ask_tier_rationale: string | null
  email_tone?: 'corporate' | 'warm' | 'executive' | null
  fit_score: number | null
  fit_rationale: string | null
  risks: string[]
  failure_reason?: string
}

export interface CompanyKnownLocation {
  name: string
  address: string | null
  city: string | null
  phone: string | null
}

export interface Company {
  id: string
  workspace_id: string
  name: string
  website: string | null
  address: string | null
  city: string | null
  province: string | null
  chain_group: string | null
  known_locations: CompanyKnownLocation[] | null
  is_hearing_clinic: boolean
  description: string | null
  industry: string | null
  enrichment_data: CompanyEnrichmentData | null
  enriched_at: string | null
  enrichment_status: EnrichmentStatus
  scheduled_enrichment_at: string | null
  campaign_id: string | null
  campaign_status: CampaignStatus | null
  campaign_has_bounced_email: boolean
  campaign_bounce_reason: string | null
  contacts: Contact[]
  created_at: string
  updated_at: string
}

export type CampaignStatus = 'draft' | 'active' | 'paused' | 'completed'
export type CampaignEmailStatus = 'draft' | 'sent' | 'replied' | 'bounced'
export type CampaignEmailType = 'welcome' | 'follow_up'

export interface CampaignReply {
  id: string
  campaign_email_id: string
  from_email: string
  from_name: string | null
  subject: string | null
  body_text: string | null
  body_html: string | null
  gmail_message_id: string | null
  received_at: string
  created_at: string
  reply_classification: string | null
  reply_classification_notes: string | null
  draft_response: string | null
}

export interface CampaignEmail {
  id: string
  campaign_id: string
  type: CampaignEmailType
  status: CampaignEmailStatus
  sequence: number
  subject: string | null
  body: string | null
  sent_at: string | null
  opened_at: string | null
  open_count: number
  bounced_at: string | null
  bounce_reason: string | null
  gmail_message_id: string | null
  replies: CampaignReply[]
  created_at: string
  updated_at: string
}

export interface CampaignLevel {
  id: string
  campaign_program_id: string
  label: string
  slug: string
  amount_cents: number
  amount_formatted: string
  benefits: string | null
  sort_order: number
  created_at: string
  updated_at: string
}

export interface CampaignProgram {
  id: string
  name: string
  org_name: string
  event_name: string
  event_dates: string
  sender_name: string
  sender_title: string | null
  sender_email: string
  sender_phone: string | null
  voice_notes: string | null
  sample_email: string | null
  is_default: boolean
  levels: CampaignLevel[]
  campaigns_count?: number
  created_at: string
  updated_at: string
}

export interface Campaign {
  id: string
  workspace_id: string
  company_id: string
  campaign_program_id: string | null
  chosen_level_id: string | null
  company?: Company
  contact: Contact | null
  program?: CampaignProgram
  chosen_level?: CampaignLevel | null
  status: CampaignStatus
  activated_at: string | null
  emails: CampaignEmail[]
  notes: string | null
  next_send_date: string | null
  has_bounced_email: boolean
  bounce_reason: string | null
  created_at: string
  updated_at: string
}

export interface AiUsageLog {
  id: string
  model: string
  operation_type: string
  input_tokens: number
  output_tokens: number
  cache_creation_input_tokens: number | null
  cache_read_input_tokens: number | null
  input_cost_millicents: number | null
  output_cost_millicents: number | null
  cache_creation_cost_millicents: number | null
  cache_read_cost_millicents: number | null
  total_cost_millicents: number
  total_tokens: number
  status: string
  latency_ms: number | null
  error_message: string | null
  prompt: string | null
  response: string | null
  usable_display_name: string | null
  formatted_operation_type: string
  created_at: string
}

export interface ComparableCompany {
  id?: string
  name: string
  website: string | null
  description: string | null
  industry: string | null
  location?: string | null
  suggested_ask_tier: string | null
}

export interface ComparableSuggestion {
  id: string
  source_company_id: string
  source_company_name: string
  name: string
  website: string | null
  description: string | null
  industry: string | null
  location: string | null
  suggested_ask_tier: string | null
  created_at: string
}

export interface ProspectCompany {
  name: string
  website: string | null
  description: string | null
  industry: string | null
  location: string | null
  suggested_ask_tier: string | null
}

export interface AnalyticsData {
  response_by_sequence: Array<{ sequence: number; sent: number; replied: number; rate: number }>
  open_rate: { opened: number; sent: number; rate: number }
  monthly_volume: Array<{ month: string; sent: number; replied: number }>
  ask_tier_distribution: Array<{ tier: string; count: number }>
  ai_cost: { total_cents: number; by_operation: Record<string, number> }
}

export interface WorkspaceMember {
  id: string
  name: string
  email: string
  role: WorkspaceRole
}

export type BatchSendStatus = 'pending' | 'sending' | 'sent' | 'failed' | 'paused' | 'cancelled'

export interface BatchDraft {
  id: string
  campaign_id: string
  company_name: string
  company_location: string | null
  contact_name: string | null
  contact_email: string
  subject: string
  sequence: number
  last_sent_at: string | null
}

export interface BatchQueueItem {
  id: string
  campaign_email_id: string
  company_name: string
  contact_name: string | null
  to_email: string
  to_name: string | null
  subject: string
  sequence: number
  status: BatchSendStatus
  send_after: string
  sent_at: string | null
  error_message: string | null
}

export interface BatchScheduleResult {
  scheduled: number
  skipped: number
  first_send_at: string | null
  last_send_at: string | null
}
